import { Component, Inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { EnrollmentAppStateStructure } from '../../shared/interfaces/enrollmentAppStateStructure.interface';
import { ENROLLMENT_APP_STATE_NAME, storeApplicationTrackingId } from '../../../shared/reducers/enrollmentApp.reducer';
import { IndividualMarketsEnrollmentSubmissionService } from '../../shared/services/enrollment/individual-markets/enrollment-submission.individual-markets.service';
import { RenameAppService } from '../saved-quotes/services/rename-app.service';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { SAVED_QUOTES_REDUCER_NAME, saveSavedQuotes } from '../saved-quotes/reducers/saved-quotes.reducer';
import { SavedQuotesStateStructure } from '../saved-quotes/interfaces/savedQuotesStateStructure.interface';
import { SaveQuoteModalConfigurationData } from './interfaces/save-quote-modal.interface';
import { firstValueFrom } from 'rxjs';
import { UserInfoService } from '../../shared/services/user-info.service';
import { SubmitAppResponse } from '../../shared/services/enrollment/enrollment-submission.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-save-quote-modal',
    templateUrl: './save-quote-modal.component.html',
    styleUrls: ['./save-quote-modal.component.scss'],
    standalone: false
})
export class SaveQuoteModalComponent {

  saveQuoteForm: UntypedFormGroup;
  createQuoteForm: UntypedFormGroup;
  firstSaveRenameForm: UntypedFormGroup;
  userEnteredQuoteName: string = '';

  /** 
   * When RESAVING an ALREADY SAVED quote
   * You'll be able to either UPDATE or CREATE a new quote
   * These form flags will determine which form you are interacting with
   */
  isUpdateFlow: boolean = false;
  isCreateFlow: boolean = false;

  newApp = false;
  hasNeverSavedOrRename: boolean = true;
  isDashboardPage: boolean = false;

  isLoading: boolean; // driver spinner display

  constructor(
    private dialogRef: MatDialogRef<SaveQuoteModalComponent>,
    private renameAppService: RenameAppService,
    private enrollSubmitIndividualService: IndividualMarketsEnrollmentSubmissionService,
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private userInfoService: UserInfoService,
    @Inject(MAT_DIALOG_DATA) public data: SaveQuoteModalConfigurationData
  ) {
    
    const savedQuotesState: SavedQuotesStateStructure = this.reducerUtilityService.getState(SAVED_QUOTES_REDUCER_NAME);

    this.firstSaveRenameForm = new FormGroup ({
      quoteName: new FormControl (this.data?.plan?.statusDescription, Validators.required)
    });
    this.saveQuoteForm = new FormGroup ({
      quoteName: new FormControl ({
        value: this.data.plan?.statusDescription || savedQuotesState.saveQuoteName || "", // dashboard:statusDescription | enroll:saveQuoteName
        disabled: true
      }, Validators.required)
    });
    this.createQuoteForm = new FormGroup({
      quoteName: new FormControl (null, Validators.required)
    });
  }

  ngOnInit() {
    const enrollmentState: EnrollmentAppStateStructure = this.reducerUtilityService.getState(ENROLLMENT_APP_STATE_NAME);
    this.newApp = !enrollmentState.appId; // No appId, no existing saved app
    this.isDashboardPage = window.location.pathname.includes('dashboard'); // This should not be matching a specific page, it's risky
    this.hasNeverSavedOrRename = this.newApp || this.isDashboardPage;
  }

  async doSave(form: UntypedFormGroup): Promise<void> {
    this.isLoading = true;
    await this.initiateSave(form);
    // this.isLoading = false; // no point in ever showing this again

    const newQuoteName = { plan: { statusDescription: this.userEnteredQuoteName } };
    const newNameData = Object.assign({}, this.data, newQuoteName);
    this.dialogRef.close(newNameData);
  }
  async initiateSave(form: UntypedFormGroup) {
    
    const savedQuotesState: SavedQuotesStateStructure = this.reducerUtilityService.getState(SAVED_QUOTES_REDUCER_NAME);
    this.userEnteredQuoteName = form.controls.quoteName.value;

    /** 
     * Only Rename
     */
    if (this.isDashboardPage) {
      //if we are on the dashboard page, just rename the application
      await this.renameAppService.renameApplication(this.data.plan.applicationTrackingId, this.userEnteredQuoteName);
      return;
    }

    /**
     * If the app doesn't exist or we want to make a new one:
     * Unset the ApplicationTrackingId - this'll force a new app creation
     */
    if (this.newApp || this.isCreateFlow) {
      // No Application Tracking ID === No app to update === New App
      this.ngRedux.dispatch(storeApplicationTrackingId(null));
    }

    /**
     * If new app, add initial save quote tracking data
     * This will allow the enrollment object to know to grab the SEP data for populating save quote
     */
    if (this.newApp || this.isCreateFlow) {

      const isLoggedIn = await firstValueFrom(this.userInfoService.isLoggedIn$);

      if (isLoggedIn) {
        // Store un-populated
        this.ngRedux.dispatch(saveSavedQuotes({
          enrollmentChannel: null,
          enrollmentFlow: this.data.modalArgs.enrollmentFlow,
          referralId: null,
          producerInd: true,
          saveQuoteName: this.userEnteredQuoteName,
          saveQuoteData: null
        }));

      }

    } else {
      /**
       * If existing app, retain previous saved quote tracking data
       * This will allow the enrollment object to know to grab the SEP data for populating save quote
       * AND
       * This will allow us to maintain the same data
       */
      this.ngRedux.dispatch(saveSavedQuotes({
        enrollmentChannel: savedQuotesState.enrollmentChannel,
        enrollmentFlow: savedQuotesState.enrollmentFlow,
        referralId: savedQuotesState.referralId,
        producerInd: savedQuotesState.producerInd,
        saveQuoteName: this.userEnteredQuoteName,
        saveQuoteData: savedQuotesState.saveQuoteData
      }));

    }

    /** SAVE */
    const saveQuoteResponse: SubmitAppResponse | HttpErrorResponse = await this.enrollSubmitIndividualService.saveQuote({
      urls: this.data.modalArgs.urls,
      quoteButtonPage: this.data.resumptionLandingPage,
      formConfig: this.data.formConfig
    });
    
    if(saveQuoteResponse as SubmitAppResponse){
      this.ngRedux.dispatch(storeApplicationTrackingId((saveQuoteResponse as SubmitAppResponse).appTrackingId));
      /** RENAME, if applicable */
      if (this.userEnteredQuoteName !== savedQuotesState.saveQuoteName) {
        await this.renameAppService.renameApplication((saveQuoteResponse as SubmitAppResponse).appTrackingId, this.userEnteredQuoteName);
      }
    }



  }
  

  openCreateQuoteForm(){
    this.isCreateFlow = true;
    this.isUpdateFlow = false;
  }

  openSaveQuoteForm(){
    this.isCreateFlow = false;
    this.isUpdateFlow = true;
  }


}
